import { useMemo } from "react";
import { FileText, Folder, Home } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  buildPageTree,
  filterPageTree,
  type PageTreeCollection,
  type PageTreeItem,
  type PageTreeNode,
} from "@/lib/brand-site-page-tree";
import type { BrandSiteCollection } from "@/lib/brand-site-api";

export const BRAND_SITE_PAGE_TREE_ID = "brand-site-preview-page-tree";

type Props = {
  pages: PageTreeItem[];
  collections?: BrandSiteCollection[] | PageTreeCollection[];
  homeSlug: string;
  activeSlug: string | null;
  missingPath: string | null;
  onNavigate: (slug: string) => void;
  /** 来自预览地址栏；空则展示整棵树 */
  query?: string;
};

export function BrandSitePageTree({
  pages,
  collections = [],
  homeSlug,
  activeSlug,
  missingPath,
  onNavigate,
  query = "",
}: Props) {
  const treeCollections = useMemo(
    () =>
      collections.map((collection) =>
        "nav_label" in collection
          ? { key: collection.key, nav_label: collection.nav_label }
          : collection,
      ),
    [collections],
  );
  const root = useMemo(
    () => buildPageTree(pages, homeSlug, treeCollections),
    [pages, homeSlug, treeCollections],
  );
  const filtered = useMemo(() => filterPageTree(root, query), [query, root]);
  const noMatch = query.trim().length > 0 && filtered.children.length === 0;

  return (
    <nav
      id={BRAND_SITE_PAGE_TREE_ID}
      className="flex max-h-80 min-h-0 flex-col"
      aria-label="页面列表"
    >
      <div className="min-h-0 flex-1 overflow-y-auto py-1">
        {noMatch ? (
          <p className="px-3 py-4 text-xs text-muted-foreground">没有匹配的页面</p>
        ) : (
          <ul className="px-1" role="tree">
            {filtered.children.map((node) => (
              <TreeRow
                key={node.key}
                node={node}
                depth={0}
                activeSlug={activeSlug}
                missingPath={missingPath}
                onNavigate={onNavigate}
              />
            ))}
          </ul>
        )}
      </div>
    </nav>
  );
}

function TreeRow({
  node,
  depth,
  activeSlug,
  missingPath,
  onNavigate,
}: {
  node: PageTreeNode;
  depth: number;
  activeSlug: string | null;
  missingPath: string | null;
  onNavigate: (slug: string) => void;
}) {
  const clickable = node.slug != null;
  const active =
    !missingPath &&
    clickable &&
    (node.slug === activeSlug ||
      (node.kind === "collection_list" &&
        Boolean(activeSlug) &&
        (activeSlug === node.slug || activeSlug === `${node.slug}*`)));
  const Icon = node.isHome ? Home : clickable ? FileText : Folder;

  return (
    <li role="treeitem" aria-selected={active}>
      {clickable ? (
        <button
          type="button"
          onClick={() => onNavigate(node.slug!)}
          aria-current={active ? "page" : undefined}
          title={`${node.path}\n${node.title}`}
          className={cn(
            "flex w-full items-start gap-1.5 py-1.5 pr-2 text-left",
            active ? "bg-accent text-accent-foreground" : "hover:bg-muted/60",
            "rounded-md",
          )}
          style={{ paddingLeft: `${depth * 12 + 8}px` }}
        >
          <Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          <TreeLabel path={node.path} title={node.title} />
        </button>
      ) : (
        <div
          className="flex items-start gap-1.5 py-1.5 pr-2 text-muted-foreground"
          style={{ paddingLeft: `${depth * 12 + 8}px` }}
          title={node.path}
        >
          <Icon className="mt-0.5 size-3.5 shrink-0" />
          <TreeLabel path={node.path} title="文件夹" />
        </div>
      )}
      {node.children.length > 0 ? (
        <ul role="group">
          {node.children.map((child) => (
            <TreeRow
              key={child.key}
              node={child}
              depth={depth + 1}
              activeSlug={activeSlug}
              missingPath={missingPath}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function TreeLabel({ path, title }: { path: string; title: string }) {
  return (
    <span className="min-w-0 flex-1">
      <span className="block font-mono text-xs leading-snug text-foreground">{path}</span>
      <span className="mt-0.5 block text-[11px] leading-snug whitespace-normal break-words text-muted-foreground">
        {title}
      </span>
    </span>
  );
}
